import type { RevisionObjectTooltip } from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionDiffModel'
import { calculateTooltipPosition } from 'features/knowledgeDocumentViewer/components/viewer/shared/PositionedTooltip'
import {
  type MouseEvent as ReactMouseEvent,
  type RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

/** Fallback tooltip size until the rendered tooltip has been measured. */
const TOOLTIP_ESTIMATED_WIDTH = 240
const TOOLTIP_ESTIMATED_HEIGHT = 64

type CursorTooltip = { content: RevisionObjectTooltip; x: number; y: number }

/**
 * Cursor-anchored tooltip state for a viewport, mirroring the viewer's
 * overlay-box tooltip flow. Position is stored relative to the viewport so a
 * tooltip rendered outside any zoom transform lands at the cursor.
 */
export function useCursorAnchoredTooltip(
  viewportRef: RefObject<HTMLDivElement>
) {
  const [tooltip, setTooltip] = useState<CursorTooltip | null>(null)
  const [measured, setMeasured] = useState<{ w: number; h: number } | null>(
    null
  )
  const tooltipRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const element = tooltipRef.current
    if (!element) return
    const w = element.offsetWidth
    const h = element.offsetHeight
    setMeasured((previous) =>
      previous && previous.w === w && previous.h === h ? previous : { w, h }
    )
  }, [tooltip?.content])

  const onHover = (content: RevisionObjectTooltip, event: ReactMouseEvent) => {
    const viewport = viewportRef.current
    if (!viewport) return
    const rect = viewport.getBoundingClientRect()
    setTooltip({
      content,
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    })
  }
  const onHoverEnd = () => setTooltip(null)

  const layout = tooltip
    ? calculateTooltipPosition(
        { x: tooltip.x, y: tooltip.y },
        measured?.w ?? TOOLTIP_ESTIMATED_WIDTH,
        measured?.h ?? TOOLTIP_ESTIMATED_HEIGHT,
        viewportRef.current?.clientWidth ?? 0,
        viewportRef.current?.clientHeight ?? 0
      )
    : null

  return { tooltip, layout, tooltipRef, onHover, onHoverEnd }
}
